import { deriveComicTitle, type ComicKind } from '@tanko/shared'

/**
 * 批次編輯的預覽。
 *
 * 批次編輯沒有復原機制，因此所見即所得是唯一能讓人放心按下去的條件：套用前逐本列出
 * 目前值與結果值，不抽樣不截斷，而且預覽與寫入呼叫同一個函式。作者欄原本被列為
 * 「明確不做」，理由是批次覆寫的風險——那個風險的來源是盲目覆寫，不是欄位本身，
 * 而這條防線對兩個欄位一樣有效。
 */

/** 可以批次編輯的欄位。封面要連縮圖一起換、分類有自己的批次動作，兩者都不在這裡。 */
export type EditableField = 'title' | 'author'

/**
 * 編輯規則。一律純文字，不接受正規表示式——沒有復原機制的操作配上一個寫錯的量詞，
 * 足以毀掉整批。
 *
 * `derive` 只給標題：資料夾路徑正是作者通常不存在的地方，推導函式本身也刻意只取標題。
 * `set` 只給作者：把一批標題全部改成同一個字串沒有意義，而且會毀掉卷號。
 */
export type EditRule =
  | { field: 'title'; kind: 'derive' }
  | { field: 'title'; kind: 'replace'; find: string; replacement: string }
  | { field: 'author'; kind: 'set'; value: string }
  | { field: 'author'; kind: 'replace'; find: string; replacement: string }

export type EditState = 'changed' | 'unchanged' | 'invalid'

export type EditResult = {
  id: string
  currentValue: string
  resultingValue: string
  state: EditState
}

/** sourceName 給相對路徑為空的直接匯入作品：推導同樣落回來源名稱，與加入時一致。 */
type Editable = { id: string; title: string; author: string; relPath: string; kind: ComicKind; sourceName?: string }

/**
 * 純文字的全部取代。
 *
 * 用 split／join 而不是 replaceAll：後者的取代字串仍會把 `$&`、`$1` 之類當成
 * 特殊語法，split／join 兩邊都完全是字面值。
 */
function replaceAllLiteral(value: string, find: string, replacement: string): string {
  return value.split(find).join(replacement)
}

export const currentValue = (comic: Editable, field: EditableField): string =>
  field === 'title' ? comic.title : comic.author

function resultingValue(comic: Editable, rule: EditRule): string {
  if (rule.kind === 'derive') {
    // 重新推導只取標題並丟棄作者：作者常常是路徑裡沒有、由使用者手動補的，
    // 用推導結果覆蓋等於刪掉那份資料。
    return deriveComicTitle(comic.relPath || comic.sourceName || '', comic.kind).title
  }
  if (rule.kind === 'set') return rule.value
  const current = currentValue(comic, rule.field)
  if (!rule.find) return current
  return replaceAllLiteral(current, rule.find, rule.replacement)
}

/**
 * 計算逐本的結果。
 *
 * 預覽與實際寫入呼叫同一個函式，因此畫面上看到的就是會寫進去的；不讓寫入路徑
 * 自己再算一次，那是兩份實作遲早分岔。
 *
 * 空值的判準兩個欄位不同：標題不能是空的，作者可以——一本作品本來就允許沒有記錄作者，
 * 因此清空作者算「會變更」而不是「無效」。
 *
 * 函式是純的：不讀資料庫、不發網路請求。
 */
export function previewEdit(comics: readonly Editable[], rule: EditRule): EditResult[] {
  return comics.map(comic => {
    const current = currentValue(comic, rule.field)
    const next = resultingValue(comic, rule).trim()
    const invalid = rule.field === 'title' && !next
    const state: EditState = invalid ? 'invalid' : next === current ? 'unchanged' : 'changed'
    return { id: comic.id, currentValue: current, resultingValue: next, state }
  })
}

export type EditSummary = { changed: number; unchanged: number; invalid: number }

export function summarise(results: readonly EditResult[]): EditSummary {
  return {
    changed: results.filter(result => result.state === 'changed').length,
    unchanged: results.filter(result => result.state === 'unchanged').length,
    invalid: results.filter(result => result.state === 'invalid').length,
  }
}
